/**
 * ConsultasAggregatePanel — aggregated cross-consulta insights.
 *
 * Renders aggregate analytics: personality distribution, common flags,
 * semantic field across analyses, stage averages, seasonality.
 */
import {
  Brain,
  ChartBar,
  Quotes,
  Warning,
  Calendar,
  TrendUp,
  Lightning,
} from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import type {
  ConsultasAggregate,
  TermoAggregate,
  FlagBucket,
} from "../lib/aggregateConsultas";

const ETAPA_LABEL: Record<string, string> = {
  conexao: "Conexão",
  perguntas_iniciais: "Perguntas Iniciais",
  investigacao_dor: "Investigação de Dor",
  diagnostico_clinico: "Diagnóstico Clínico",
  oferta: "Oferta",
  fechamento: "Fechamento",
  geral: "Geral",
};

const FLAG_LABEL: Record<string, string> = {
  PULO_DESEJO: "Pulou desejo",
  ABERTURA_GENERICA: "Abertura genérica",
  SEM_SIGNIFICADO: "Sem significado",
  DOMINANCIA_PERDIDA: "Dominância perdida",
  URGENCIA_INVENTADA: "Urgência inventada",
  MICRO_SOLUCAO: "Micro-solução",
  PRECO_PRECOCE: "Preço precoce",
  PROLIXIDADE: "Prolixidade",
};

const GRADE_LABEL: Record<string, string> = {
  excelente: "Excelente",
  bom: "Bom",
  intermediario: "Treinar",
  fraco: "Fraco",
  critico: "Recomeçar",
};

const GRADE_CLS: Record<string, string> = {
  excelente: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  bom: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  intermediario: "bg-yellow-500/15 text-yellow-400 border-yellow-500/30",
  fraco: "bg-orange-500/15 text-orange-400 border-orange-500/30",
  critico: "bg-red-500/15 text-red-400 border-red-500/30",
};

function SectionHeader({
  icon: Icon,
  title,
  hint,
}: {
  icon: React.ComponentType<{ className?: string; weight?: "duotone" | "fill" | "regular" | "bold" }>;
  title: string;
  hint?: string;
}) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon className="w-5 h-5 text-primary" weight="duotone" aria-hidden="true" />
      <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      {hint && <span className="text-xs text-muted-foreground">· {hint}</span>}
    </div>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-muted-foreground italic">{children}</p>;
}

function TermosList({ termos, color }: { termos: TermoAggregate[]; color: string }) {
  if (termos.length === 0) return <EmptyHint>Nenhum termo recorrente ainda.</EmptyHint>;
  const top = termos.slice(0, 12);
  const max = top[0]?.totalFreq ?? 1;
  return (
    <ul className="space-y-1.5">
      {top.map((t) => (
        <li key={t.termo} className="flex items-center gap-2">
          <span className="text-sm text-foreground flex-1 truncate" title={t.exemploFrase}>
            {t.termo}
          </span>
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-16 sm:w-24 h-1.5 rounded bg-muted/40 overflow-hidden">
              <div
                className={cn("h-full rounded transition-all", color)}
                style={{ width: `${Math.max(8, (t.totalFreq / max) * 100)}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground tabular-nums w-10 text-right">
              {t.totalFreq}×
            </span>
            <span className="hidden sm:inline text-[10px] text-muted-foreground/70 w-12 text-right">
              {t.consultas} c.
            </span>
          </div>
        </li>
      ))}
    </ul>
  );
}

function FlagRow({ bucket, max, top }: { bucket: FlagBucket; max: number; top?: boolean }) {
  return (
    <li
      className={cn(
        "space-y-1.5 rounded-md p-2 -mx-2 transition-colors",
        top && "ring-1 ring-rose-500/20 bg-rose-500/[0.03]",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className={cn("text-sm text-foreground", top && "font-medium")}>
          {FLAG_LABEL[bucket.flag] ?? bucket.flag}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {bucket.impactoAlto > 0 && (
            <Badge variant="destructive" className="text-[10px] py-0 h-5">
              {bucket.impactoAlto} alto
            </Badge>
          )}
          {bucket.impactoMedio > 0 && (
            <Badge variant="secondary" className="text-[10px] py-0 h-5">
              {bucket.impactoMedio} médio
            </Badge>
          )}
          <span className="text-xs text-muted-foreground tabular-nums">{bucket.count}×</span>
        </div>
      </div>
      <div className="h-1.5 rounded bg-muted/40 overflow-hidden">
        <div
          className="h-full rounded bg-rose-500/70 transition-all"
          style={{ width: `${Math.max(4, (bucket.count / max) * 100)}%` }}
        />
      </div>
      {bucket.exemplos[0] && (
        <p className="text-[11px] text-muted-foreground italic line-clamp-1 pl-1">
          "{bucket.exemplos[0]}"
        </p>
      )}
    </li>
  );
}

export function ConsultasAggregatePanel({ aggregate }: { aggregate: ConsultasAggregate }) {
  const { medias, personalidades, flags, etapaCriticaFreq, gargalos, campoSemantico, sazonalidade } =
    aggregate;

  const maxFlag = flags[0]?.count ?? 1;
  const maxEtapa = etapaCriticaFreq[0]?.count ?? 1;
  const maxMes = Math.max(1, ...sazonalidade.porMes.map((m) => m.count));
  const maxDia = Math.max(1, ...sazonalidade.porDiaSemana.map((d) => d.count));

  return (
    <div className="space-y-5">
      {/* ── Período (meta) ── */}
      {(aggregate.intervaloDatas.inicio || aggregate.intervaloDatas.fim) && (
        <p className="text-xs text-muted-foreground -mt-2">
          Período:{" "}
          <span className="text-foreground/80">
            {aggregate.intervaloDatas.inicio
              ? new Date(aggregate.intervaloDatas.inicio).toLocaleDateString("pt-BR")
              : "—"}
          </span>
          {aggregate.intervaloDatas.fim && (
            <>
              {" "}
              <span className="text-muted-foreground/60">→</span>{" "}
              <span className="text-foreground/80">
                {new Date(aggregate.intervaloDatas.fim).toLocaleDateString("pt-BR")}
              </span>
            </>
          )}
        </p>
      )}

      {/* ── Resumo geral ── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 print-avoid-break">
        <Card className="p-4">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Consultas</p>
          <p className="text-2xl font-semibold text-foreground mt-1 tabular-nums">
            {aggregate.total}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {aggregate.comAnaliseValida} com análise
          </p>
        </Card>
        <Card className="p-4">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
            Method total méd.
          </p>
          <p className="text-2xl font-semibold text-foreground mt-1 tabular-nums">
            {medias.methodTotalAvg != null ? medias.methodTotalAvg.toFixed(1) : "—"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">de 60</p>
        </Card>
        <Card className="p-4">
          <p className="text-[11px] text-muted-foreground uppercase tracking-wide">
            Score final méd.
          </p>
          <p className="text-2xl font-semibold text-foreground mt-1 tabular-nums">
            {medias.scoreFinalAvg != null ? medias.scoreFinalAvg.toFixed(0) : "—"}
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">de 100</p>
        </Card>
      </div>

      {/* ── Distribuição grades ── */}
      {Object.keys(medias.gradeDistribution).length > 0 && (
        <Card className="p-5">
          <SectionHeader icon={TrendUp} title="Distribuição de desempenho" />
          <div className="flex flex-wrap gap-2">
            {Object.entries(medias.gradeDistribution).map(([grade, count]) => (
              <Badge
                key={grade}
                variant="outline"
                className={cn("gap-1.5", GRADE_CLS[grade])}
              >
                {GRADE_LABEL[grade] ?? grade}
                <span className="font-semibold">{count}</span>
              </Badge>
            ))}
          </div>
        </Card>
      )}

      {/* ── Médias por etapa ── */}
      {Object.keys(medias.porEtapa).length > 0 && (
        <Card className="p-5">
          <SectionHeader icon={ChartBar} title="Médias por etapa" hint="0–10" />
          <ul className="space-y-2.5">
            {Object.entries(medias.porEtapa)
              .sort(([, a], [, b]) => (b ?? 0) - (a ?? 0))
              .map(([slug, score]) => (
                <li key={slug} className="flex items-center gap-3">
                  <span className="text-sm text-foreground w-44 shrink-0">
                    {ETAPA_LABEL[slug] ?? slug}
                  </span>
                  <Progress value={(score ?? 0) * 10} className="h-2 flex-1" />
                  <span className="text-sm text-foreground tabular-nums w-10 text-right">
                    {(score ?? 0).toFixed(1)}
                  </span>
                </li>
              ))}
          </ul>
        </Card>
      )}

      {/* ── Personalidades ── */}
      <Card className="p-5">
        <SectionHeader
          icon={Brain}
          title="Personalidades dos pacientes"
          hint={`${personalidades.length} tipo(s) detectado(s)`}
        />
        {personalidades.length === 0 ? (
          <EmptyHint>Personalidade ainda não detectada nas análises.</EmptyHint>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {personalidades.map((p) => (
              <div
                key={p.codigo}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/30 border border-border/40"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">
                    <span className="font-mono text-primary">{p.codigo}</span> · {p.nome}
                  </p>
                  {p.centroIntel && (
                    <p className="text-[11px] text-muted-foreground capitalize">
                      Centro {p.centroIntel}
                    </p>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <p className="text-lg font-semibold text-foreground tabular-nums">{p.count}</p>
                  <p className="text-[11px] text-muted-foreground tabular-nums">
                    {p.pct.toFixed(0)}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ── Padrões a corrigir (flags) ── */}
      <Card className="p-5 print-avoid-break">
        <SectionHeader
          icon={Warning}
          title="Padrões a corrigir"
          hint="flags recorrentes"
        />
        {flags.length === 0 ? (
          <EmptyHint>Nenhuma flag crítica recorrente. Boa execução.</EmptyHint>
        ) : (
          <ul className="space-y-3">
            {flags.map((f, i) => (
              <FlagRow key={f.flag} bucket={f} max={maxFlag} top={i === 0} />
            ))}
          </ul>
        )}
      </Card>

      {/* ── Etapa crítica ── */}
      {etapaCriticaFreq.length > 0 && (
        <Card className="p-5 print-avoid-break">
          <SectionHeader icon={Lightning} title="Etapas críticas mais frequentes" />
          <ul className="space-y-2">
            {etapaCriticaFreq.map((e) => (
              <li key={e.etapa} className="flex items-center gap-3">
                <span className="text-sm text-foreground w-44 shrink-0">
                  {ETAPA_LABEL[e.etapa] ?? e.etapa}
                </span>
                <Progress value={(e.count / maxEtapa) * 100} className="h-2 flex-1" />
                <span className="text-xs text-muted-foreground tabular-nums w-16 text-right">
                  {e.count} ({e.pct.toFixed(0)}%)
                </span>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ── Pontos de fricção ── */}
      {gargalos.length > 0 && (
        <Card className="p-5 print-avoid-break">
          <SectionHeader icon={Warning} title="Pontos de fricção" />
          <ul className="space-y-3">
            {gargalos.map((g) => (
              <li key={g.etapa} className="space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">
                    {ETAPA_LABEL[g.etapa] ?? g.etapa}
                  </span>
                  <Badge variant="outline" className="text-xs">
                    {g.count}× repetições
                  </Badge>
                </div>
                {g.exemplos.slice(0, 2).map((ex, i) => (
                  <p key={i} className="text-xs text-muted-foreground italic line-clamp-2 pl-1">
                    "{ex}"
                  </p>
                ))}
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ── Campo semântico agregado ── */}
      <Card className="p-5">
        <SectionHeader
          icon={Quotes}
          title="Campo semântico agregado"
          hint="palavras recorrentes entre todas as consultas"
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div>
            <p className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-rose-500/80" aria-hidden="true" />
              Dor
            </p>
            <TermosList termos={campoSemantico.dor} color="bg-rose-500/70" />
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500/80" aria-hidden="true" />
              Desejo
            </p>
            <TermosList termos={campoSemantico.desejo} color="bg-emerald-500/70" />
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-amber-500/80" aria-hidden="true" />
              Objeção
            </p>
            <TermosList termos={campoSemantico.objecao} color="bg-amber-500/70" />
          </div>
        </div>

        {campoSemantico.palavrasChaveCentrais.length > 0 && (
          <div className="mt-5 pt-4 border-t border-border/40">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
              Palavras-chave centrais recorrentes
            </p>
            <div className="flex flex-wrap gap-1.5">
              {campoSemantico.palavrasChaveCentrais.slice(0, 15).map((p) => (
                <Badge key={p.termo} variant="secondary" className="gap-1.5">
                  {p.termo}
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {p.count}×
                  </span>
                </Badge>
              ))}
            </div>
          </div>
        )}

        {campoSemantico.expressoesMarcantes.length > 0 && (
          <div className="mt-4 pt-4 border-t border-border/40">
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
              Expressões marcantes repetidas
            </p>
            <ul className="space-y-1">
              {campoSemantico.expressoesMarcantes.slice(0, 8).map((e) => (
                <li
                  key={e.texto}
                  className="text-xs text-foreground flex items-center justify-between gap-2"
                >
                  <span className="truncate">"{e.texto}"</span>
                  <span className="text-muted-foreground tabular-nums shrink-0">{e.count}×</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      {/* ── Sazonalidade ── */}
      <Card className="p-5">
        <SectionHeader icon={Calendar} title="Sazonalidade" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
              Por mês
            </p>
            {sazonalidade.porMes.length === 0 ? (
              <EmptyHint>Sem dados temporais.</EmptyHint>
            ) : (
              <ul className="space-y-1.5">
                {sazonalidade.porMes.map((m) => (
                  <li key={m.mes} className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground w-12 shrink-0">{m.mes}</span>
                    <div className="flex-1 h-1.5 rounded bg-muted/40 overflow-hidden">
                      <div
                        className="h-full bg-primary/70 rounded"
                        style={{ width: `${(m.count / maxMes) * 100}%` }}
                      />
                    </div>
                    <span className="text-xs text-foreground tabular-nums w-8 text-right">
                      {m.count}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-2 uppercase tracking-wide">
              Dia da semana
            </p>
            <ul className="space-y-1.5">
              {sazonalidade.porDiaSemana.map((d) => (
                <li key={d.dia} className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground w-12 shrink-0 capitalize">
                    {d.dia}
                  </span>
                  <div className="flex-1 h-1.5 rounded bg-muted/40 overflow-hidden">
                    <div
                      className="h-full bg-primary/70 rounded"
                      style={{ width: `${(d.count / maxDia) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs text-foreground tabular-nums w-8 text-right">
                    {d.count}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </Card>
    </div>
  );
}
